import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type Role = 'ADMIN' | 'RESIDENT' | 'VENDOR' | 'PLATFORM_ADMIN';

interface PrivateRouteProps {
  children: React.ReactNode;
  /** When provided, only users with one of these roles may access the route. */
  allowedRoles?: Role[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const { user, isAuthLoading } = useAuth();
  const location = useLocation();

  // Wait for auth to be ready before checking
  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-[#16161a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#22d3ee]"></div>
      </div>
    );
  }

  if (!user) {
    // Not logged in — redirect to the appropriate login page
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Role check — send unauthorised users to their own login, not into the app
  if (allowedRoles && !allowedRoles.includes(user.role as Role)) {
    const destination = user.role === 'PLATFORM_ADMIN' ? '/admin/dashboard' : '/login';
    return <Navigate to={destination} replace />;
  }

  // Only ADMIN users need to select a property group
  if (user.role === 'ADMIN' && location.pathname !== '/select-group') {
    const selectedGroupId = localStorage.getItem('selectedPropertyGroup');
    if (!selectedGroupId) {
      return <Navigate to="/select-group" replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute; 